import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../lib/api';
import { hasSeenExtensionInstall, markExtensionInstallSeen } from '../../lib/extension-install';

// Matches the getCurrentUser select + /orgs shape used elsewhere in the app.
interface Me {
  isPractitioner: boolean;
  totpEnabled: boolean;
  checklistDismissedAt: string | null;
}
interface OrgRow {
  id: string;
  name: string;
  slug: string;
  role: string;
}
interface ChannelLite {
  id: string;
  name: string;
  type: string;
}
interface InviteLite {
  id: string;
  isActive: boolean;
}

interface Props {
  onInvite?: () => void;
  onCreateGroup?: () => void;
  onGoToSettings?: () => void;
}

/**
 * GetStartedChecklist — rendered on the empty workspace for users who
 * haven't completed all onboarding actions. Each item's checked state is
 * derived from real backend state so it auto-updates as the user completes
 * tasks elsewhere in the app.
 */
export default function GetStartedChecklist({ onInvite, onCreateGroup, onGoToSettings }: Props) {
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Device-local rather than server-side: "has this browser been shown the
  // install page" is a per-browser fact, and the extension only exists on the
  // browser the user is sitting at.
  const [extensionSeen, setExtensionSeen] = useState(hasSeenExtensionInstall);

  const { data: me } = useQuery<Me>({
    queryKey: ['me-onboarding'],
    queryFn: async () => {
      const { data } = await api.get('/api/v1/users/me');
      return data.data as Me;
    },
  });

  const { data: orgs } = useQuery<OrgRow[]>({
    queryKey: ['orgs'],
    queryFn: async () => {
      const { data } = await api.get('/api/v1/orgs');
      return data.data as OrgRow[];
    },
  });

  const adminOrg = useMemo(
    () => orgs?.find((o) => o.role === 'OWNER' || o.role === 'ADMIN') ?? orgs?.[0],
    [orgs],
  );

  const { data: channels } = useQuery<ChannelLite[]>({
    queryKey: ['channels', adminOrg?.id],
    queryFn: async () => {
      const { data } = await api.get(`/api/v1/orgs/${adminOrg!.id}/channels`);
      return data.data as ChannelLite[];
    },
    enabled: !!adminOrg?.id,
  });

  const { data: invites } = useQuery<InviteLite[]>({
    queryKey: ['org-invites', adminOrg?.id],
    queryFn: async () => {
      const { data } = await api.get(`/api/v1/orgs/${adminOrg!.id}/invites`);
      return data.data as InviteLite[];
    },
    enabled: !!adminOrg?.id,
  });

  const dismiss = useMutation({
    mutationFn: async () => {
      await api.post('/api/v1/users/me/checklist/dismiss');
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me-onboarding'] }),
    onError: () => toast.error('Could not dismiss the checklist.'),
  });

  if (!me) return null;
  if (me.checklistDismissedAt) return null;

  const hasPractice = !!adminOrg;
  const hasGroup = (channels ?? []).some((c) => c.type === 'PRIVATE');
  const hasInvite = (invites ?? []).some((i) => i.isActive);
  const items = [
    me.isPractitioner && {
      id: 'mfa',
      done: me.totpEnabled,
      title: 'Enable two-factor authentication',
      hint: 'Required for practitioners before you can open channels. Takes under a minute.',
      cta: 'Open settings',
      onClick: onGoToSettings,
    },
    {
      id: 'practice',
      done: hasPractice,
      title: 'Create your practice',
      hint: 'Set up the workspace your team will join.',
      cta: 'Create practice',
      onClick: onCreateGroup, // Opens the sidebar's create-practice trigger in the current UI.
    },
    {
      id: 'group',
      done: hasGroup,
      title: 'Create your first group',
      hint: 'Groups are where your team chats. Try "reception", "clinical", or a team name.',
      cta: 'Create group',
      onClick: onCreateGroup,
    },
    {
      id: 'invite',
      done: hasInvite,
      title: 'Invite your team',
      hint: 'Share an invite link so colleagues can sign in.',
      cta: 'Invite people',
      onClick: onInvite,
    },
    {
      // Last on purpose: it is the step that makes the product worth using,
      // so it should still be sitting there once the workspace admin has
      // finished the mechanical setup above.
      id: 'extension',
      done: extensionSeen,
      title: 'Install the Chrome extension',
      hint: 'Puts ClinikChat inside Cliniko, Jane, Nookal, Power Diary, and Halaxy — chat without leaving the patient record.',
      cta: 'Get the extension',
      onClick: () => {
        // Optimistic: the web app has no way to observe the install itself
        // (see lib/extension-install.ts), so opening the page is the signal.
        markExtensionInstallSeen();
        setExtensionSeen(true);
        window.open('/extension', '_blank', 'noopener,noreferrer');
      },
    },
  ].filter(Boolean) as Array<{
    id: string;
    done: boolean;
    title: string;
    hint: string;
    cta: string;
    onClick?: () => void;
  }>;

  if (items.every((i) => i.done)) return null;

  return (
    <div className="mt-8 w-full max-w-md rounded-2xl border border-warm-border bg-white p-5 text-left shadow-warm-md dark:border-warm-dark-border dark:bg-warm-dark-surface">
      <div className="mb-3 flex items-start justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-primary">
            Get started
          </p>
          <h3 className="mt-0.5 text-sm font-semibold text-warm-gray-900 dark:text-white">
            A few things to finish setting up
          </h3>
        </div>
        <button
          type="button"
          onClick={() => dismiss.mutate()}
          aria-label="Dismiss checklist"
          className="rounded-md p-1 text-warm-gray-400 hover:bg-warm-gray-100 hover:text-warm-gray-600 dark:hover:bg-white/10"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <ul className="space-y-1.5">
        {items.map((item) => {
          const expanded = expandedId === item.id && !item.done;
          return (
            <li key={item.id}>
              <button
                type="button"
                disabled={item.done}
                onClick={() => setExpandedId(expanded ? null : item.id)}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors hover:bg-warm-gray-50 disabled:cursor-default dark:hover:bg-white/[0.04]"
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                    item.done
                      ? 'border-accent-green bg-accent-green text-white'
                      : 'border-warm-border dark:border-warm-dark-border'
                  }`}
                >
                  {item.done ? (
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : null}
                </span>
                <span
                  className={
                    item.done
                      ? 'text-warm-gray-500 line-through dark:text-warm-gray-500'
                      : 'text-warm-gray-800 dark:text-warm-gray-200'
                  }
                >
                  {item.title}
                </span>
              </button>
              {expanded && (
                <div className="ml-7 mt-1 rounded-lg bg-warm-gray-50 p-3 text-xs text-warm-gray-600 dark:bg-warm-dark-elevated dark:text-warm-gray-400">
                  <p>{item.hint}</p>
                  {item.onClick && (
                    <button
                      type="button"
                      onClick={item.onClick}
                      className="mt-2 rounded-lg bg-primary px-3 py-1 text-xs font-semibold text-white hover:bg-primary-hover"
                    >
                      {item.cta}
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
